import { computed } from 'vue';
import { useWindowSize } from '@vueuse/core';

/**
 * 클라이언트 타입 판별 composable
 *
 * 3가지 클라이언트:
 *   - App (WebView): window.ReactNativeWebView 존재
 *   - Mobile Web: 브라우저 + 뷰포트 ≤768px
 *   - PC Web: 브라우저 + 뷰포트 >768px
 *
 * 분기 그룹:
 *   - UI 분기 (isMobileUi): App + Mobile → 모바일 UI / PC → 데스크톱 UI
 *   - 기능 분기 (isApp): App → 브릿지 / Mobile + PC → 웹 방식
 */
export const useClientType = () => {
  const { width: windowWidth } = useWindowSize();

  /** App WebView 여부 (ReactNativeWebView 또는 __APP_VERSION__ inject로 판별) */
  const isApp = computed(() => !!window.ReactNativeWebView || !!window.__APP_VERSION__);

  /** 앱 버전 (NmWebView에서 inject). null이면 inject 미지원 구버전 앱이거나 브라우저 (isApp으로 구분) */
  const appVersion = computed(() => window.__APP_VERSION__ ?? null);

  /** 모바일 뷰포트 여부 (≤768px) */
  const isMobileSize = computed(() => windowWidth.value <= 768);

  /** UI 분기: 모바일 UI를 사용할 것인가 (App + Mobile Web) */
  const isMobileUi = computed(() => isApp.value || isMobileSize.value);


  /** 서버 타입 (NmWebView에서 inject: 'development' | 'staging' | 'production') */
  const serverType = computed(() => window.__SERVER_TYPE__ ?? null);

  /** 개발 앱 여부 (앱 WebView + development 서버) */
  const isDevApp = computed(() => isApp.value && serverType.value === 'development');

  /** 스테이징 앱 여부 (앱 WebView + staging 서버) */
  const isStagingApp = computed(() => isApp.value && serverType.value === 'staging');

  /** Safe Area Insets (NmWebView에서 inject) */
  const safeAreaInsets = computed(() => window.__SAFE_AREA_INSETS__ ?? { top: 0, bottom: 0 });

  return {
    /** App WebView 여부 */
    isApp,
    /** 앱 버전 정보 (예: '1.3.2'). null = 구버전 앱 또는 브라우저 */
    appVersion,
    /** 모바일 뷰포트 여부 */
    isMobileSize,
    /** UI 분기: App + Mobile Web = true */
    isMobileUi,
    /** 서버 타입 ('development' | 'production' | null) */
    serverType,
    /** 개발 앱 WebView 여부 */
    isDevApp,
    /** 스테이징 앱 WebView 여부 */
    isStagingApp,
    /** Safe Area Insets { top, bottom } */
    safeAreaInsets,
  };
};
